import {
  useDeleteOrganization,
  useOrganization,
  useUpdateOrganization,
} from "@/api/organization";
import { useUserInfo } from "@/api/user";
import { useOrgUsers } from "@/api/users";
import { SubscriptionSection } from "@/components/billing/SubscriptionSection";
import { DeleteOrganizationDialog } from "@/components/DeleteOrganizationDialog/DeleteOrganizationDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PLANS } from "@/constants/plans";
import { cn } from "@/utils";
import { Spinner } from "@cohesive/ui-components";
import { AlertCircle, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import * as z from "zod";
import {
  DirtyField,
  GroupTile,
  NESTED_DIALOG_Z,
  outlineDestructiveClassName,
  PaneBody,
  PaneSection,
  SettingRow,
  smButtonClassName,
} from "./components/manage-pane-primitives";
import { OrgSSOSection } from "./OrgSSOSection";

/**
 * Organization — the Manage modal's Settings·Organization scope. The org's
 * identity (name, logo), how its people sign in, and the one irreversible
 * verb, kept at the foot behind a typed confirmation.
 */

const emojiRegex = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;

function validateName(value: string): string | null {
  if (value.trim().length < 2) {
    return "Organization name must be at least 2 characters.";
  }
  if (value.length > 64) {
    return "Organization name exceeds maximum length of 64 characters.";
  }
  if (emojiRegex.test(value)) {
    return "Organization name cannot contain emojis.";
  }
  return null;
}

function validateLogoUrl(value: string): string | null {
  if (!value.trim()) return null;
  const isHttpsUrl =
    value.startsWith("https://") && z.string().url().safeParse(value).success;
  return isHttpsUrl ? null : "Logo URL must be a valid HTTPS URL.";
}

/** Live preview of the logo URL — the only way to know the link resolves. */
const LogoPreview: React.FC<{ url: string }> = ({ url }) => {
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");

  useEffect(() => {
    setState("loading");
  }, [url]);

  if (!url) return null;

  if (validateLogoUrl(url)) {
    return (
      <span className="inline-flex size-9 items-center justify-center rounded-md border border-n-10 bg-n-05">
        <AlertCircle className="size-4 text-spectrum-red" />
      </span>
    );
  }

  return (
    <span className="relative inline-flex size-9 items-center justify-center overflow-hidden rounded-md border border-n-10 bg-n-05">
      {state === "loading" && (
        <Loader2 className="size-4 animate-spin text-n-40" />
      )}
      {state === "error" && (
        <AlertCircle className="size-4 text-spectrum-red" />
      )}
      <img
        src={url}
        alt=""
        className={state === "ok" ? "size-full object-cover" : "hidden"}
        onLoad={() => setState("ok")}
        onError={() => setState("error")}
      />
    </span>
  );
};

export const OrgSettings: React.FC = () => {
  const { data: organization, isLoading } = useOrganization();
  const { data: userInfo } = useUserInfo();
  const { data: users } = useOrgUsers();
  const updateOrganization = useUpdateOrganization();
  const deleteOrganization = useDeleteOrganization();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLogoDialog, setShowLogoDialog] = useState(false);
  /** Mirrors the dialog's field so the preview tracks what is being typed. */
  const [logoDraft, setLogoDraft] = useState("");

  const isEnterprise = userInfo?.subscription?.plan_name === PLANS.ENTERPRISE;

  const save = async (
    patch: { name?: string; logo_url?: string },
    validationError: string | null,
  ) => {
    // Rejecting is the contract DirtyField reads: it only keeps the Save
    // control (and the typed value) when this throws. Returning quietly on a
    // failed write marked the field clean and the edit looked saved.
    if (validationError) {
      toast.error(validationError);
      throw new Error(validationError);
    }
    try {
      await updateOrganization.mutateAsync(patch);
      toast.success("Organization updated.");
    } catch (error) {
      toast.error("Failed to update the organization. Please try again.");
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  const memberCount = users?.length ?? 0;
  const planName = userInfo?.subscription?.plan_display_name;

  return (
    <PaneBody>
      <div className="flex items-center gap-3 px-2.5">
        {organization?.logo_url ? (
          <img
            src={organization.logo_url}
            alt=""
            className="size-12 shrink-0 rounded-md border border-n-10 object-cover"
          />
        ) : (
          <GroupTile
            name={organization?.name ?? "?"}
            className="size-12 text-lg"
          />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-lg font-medium text-n-90">
            {organization?.name || "—"}
          </h3>
          <div className="truncate text-sm text-n-50">
            {[
              planName ? `${planName} plan` : null,
              memberCount > 0
                ? `${memberCount} member${memberCount === 1 ? "" : "s"}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <DirtyField
          label="Organization name"
          initial={organization?.name ?? ""}
          isSaving={updateOrganization.isPending}
          onSave={(value) => save({ name: value.trim() }, validateName(value))}
        />
      </div>

      <PaneSection head="Workspace">
        {/* No preview here — the masthead at the top of the pane already is
            the logo, and showing it twice made the row read as a form. */}
        <SettingRow label="Logo" desc="Shown on invites and shared documents">
          <Button
            size="sm"
            variant="secondary"
            className={smButtonClassName}
            onClick={() => {
              setLogoDraft(organization?.logo_url ?? "");
              setShowLogoDialog(true);
            }}
          >
            {organization?.logo_url ? "Change" : "Add"}
          </Button>
        </SettingRow>
        {isEnterprise && <OrgSSOSection />}
      </PaneSection>

      <SubscriptionSection />

      <PaneSection head="Danger zone">
        <SettingRow
          label="Delete organization"
          desc="Removes the organization and everything in it. This cannot be undone."
        >
          <Button
            size="sm"
            variant="ghost"
            className={cn(smButtonClassName, outlineDestructiveClassName)}
            onClick={() => setShowDeleteDialog(true)}
          >
            Delete organization
          </Button>
        </SettingRow>
      </PaneSection>

      <Dialog open={showLogoDialog} onOpenChange={setShowLogoDialog}>
        <DialogContent
          className={NESTED_DIALOG_Z}
          overlayClassName={NESTED_DIALOG_Z}
        >
          <DialogHeader>
            <DialogTitle>Organization logo</DialogTitle>
            <DialogDescription>
              Point at an image and we&apos;ll show it on invites and shared
              documents. It has to be a public HTTPS link.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-3">
            <div className="min-w-0 flex-1">
              <DirtyField
                label="Logo URL"
                initial={organization?.logo_url ?? ""}
                onChange={setLogoDraft}
                isSaving={updateOrganization.isPending}
                onSave={async (value) => {
                  await save(
                    { logo_url: value.trim() || undefined },
                    validateLogoUrl(value),
                  );
                  setShowLogoDialog(false);
                }}
              />
            </div>
            <div className="pb-1">
              <LogoPreview url={logoDraft} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* The dialog opens on a non-null organization — that is its contract. */}
      <DeleteOrganizationDialog
        organization={showDeleteDialog ? (organization ?? null) : null}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirmDelete={async () => {
          try {
            await deleteOrganization.mutateAsync();
            toast.success("Organization deleted.");
          } catch {
            toast.error("Failed to delete the organization.");
          }
        }}
        isLoading={deleteOrganization.isPending}
      />
    </PaneBody>
  );
};

export default OrgSettings;
